// FX / metals market-hours helper — purely local computation, no network.
//
// Convention: FX (and spot metals on OANDA / Capital.com) trade ~24/5.
// The globally accepted week open/close is **17:00 America/New_York**:
//   * Sunday 17:00 NY  →  market OPEN
//   * Friday 17:00 NY  →  market CLOSED
// DST-safe because we resolve NY wall time via `Intl.DateTimeFormat`
// with `timeZone: "America/New_York"` — no hard-coded ±4/±5 offset.

const WEEKDAY_INDEX = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};

// Cache one formatter (Intl construction is expensive).
const NY_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  hourCycle: "h23",
  weekday: "short",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

// UTC formatter for the label display ("Fri 21:00 UTC")
const UTC_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  hourCycle: "h23",
  weekday: "short",
  hour: "2-digit",
  minute: "2-digit",
});

function nyParts(date) {
  const parts = Object.fromEntries(
    NY_FMT.formatToParts(date).map((p) => [p.type, p.value]),
  );
  return {
    weekday: WEEKDAY_INDEX[parts.weekday],
    hour: parseInt(parts.hour, 10),
    minute: parseInt(parts.minute, 10),
    year: parseInt(parts.year, 10),
    month: parseInt(parts.month, 10),
    day: parseInt(parts.day, 10),
  };
}

// Given the desired NY wall-clock instant (year/month/day/hour/minute),
// return the corresponding UTC Date. DST-safe: we make a rough guess and
// then correct once (and once more for the twice-a-year fold), so this
// converges even when the target lands inside a DST boundary window.
function nyWallToUtc(year, month, day, hour, minute) {
  let guess = new Date(Date.UTC(year, month - 1, day, hour, minute));
  for (let i = 0; i < 3; i++) {
    const p = nyParts(guess);
    const deltaMin =
      (p.hour * 60 + p.minute) - (hour * 60 + minute)
      + ((p.day - day) * 24 * 60)
      + ((p.month - month) * 30 * 24 * 60);
    if (deltaMin === 0) break;
    guess = new Date(guess.getTime() - deltaMin * 60_000);
  }
  return guess;
}

// Build the UTC Date of the NEXT `targetWeekday` at 17:00 NY, at or after
// `fromDate`. `p.day + daysAhead` is passed straight to Date.UTC (which
// handles month rollover) — we do NOT round-trip via UTC-midnight
// because midnight-UTC on target day is still the PREVIOUS calendar day
// in New York and that would silently shift us back a day.
function nextNyWeeklyBoundary(fromDate, targetWeekday) {
  const p = nyParts(fromDate);
  let daysAhead = (targetWeekday - p.weekday + 7) % 7;
  let utc = nyWallToUtc(p.year, p.month, p.day + daysAhead, 17, 0);
  if (utc.getTime() <= fromDate.getTime()) {
    // Same-day target already past → jump 7 days.
    utc = nyWallToUtc(p.year, p.month, p.day + daysAhead + 7, 17, 0);
  }
  return utc;
}

/**
 * Compute the FX / spot-metals market state at `now` (default: system clock).
 *
 * Returns:
 *   open        — boolean
 *   nextChange  — UTC Date of the next open⇄close boundary
 *   label       — human-readable, e.g. "closes Fri 21:00 UTC" or "opens Sun 22:00 UTC"
 *   pillText    — full pill copy, e.g. "Market open · closes Fri 21:00 UTC"
 */
export function fxMarketState(now = new Date()) {
  const p = nyParts(now);

  // OPEN window: Sunday 17:00 NY (inclusive) → Friday 17:00 NY (exclusive).
  // Equivalently CLOSED when:
  //   * NY weekday is Saturday, OR
  //   * NY weekday is Sunday AND hour < 17, OR
  //   * NY weekday is Friday AND hour >= 17.
  let open;
  if (p.weekday === 6) open = false;                                // Sat
  else if (p.weekday === 0 && p.hour < 17) open = false;            // Sun morning
  else if (p.weekday === 5 && p.hour >= 17) open = false;           // Fri after 5pm NY
  else open = true;

  // Compute the next boundary UTC.
  let nextChange;
  if (open) {
    // Next boundary is the coming Friday 17:00 NY. If we're mid-Friday
    // pre-close, that's today at 17:00 NY.
    nextChange = nextNyWeeklyBoundary(now, 5);
  } else {
    // Next boundary is the coming Sunday 17:00 NY.
    nextChange = nextNyWeeklyBoundary(now, 0);
  }

  // Format the boundary UTC as "Fri 21:00 UTC".
  const parts = Object.fromEntries(
    UTC_FMT.formatToParts(nextChange).map((x) => [x.type, x.value]),
  );
  const utcLabel = `${parts.weekday} ${parts.hour}:${parts.minute} UTC`;
  const label = open ? `closes ${utcLabel}` : `opens ${utcLabel}`;
  const pillText = open
    ? `Market open · ${label}`
    : `Market closed · ${label}`;

  return { open, nextChange, label, pillText };
}

// Helper for tests / manual verification.
export function _internal_nyParts(d) { return nyParts(d); }
export function _internal_nyWallToUtc(y, m, d, h, mi) {
  return nyWallToUtc(y, m, d, h, mi);
}
